import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SdrRoiHeroSection from '@/components/SdrRoiHeroSection';
import TestimonialSection from '@/components/TestimonialSection';
import PersonaToggle from '@/components/PersonaToggle';
import AccountabilitySection from '@/components/AccountabilitySection';
import FeatureShowcase from '@/components/FeatureShowcase';
import AllInOnePlatform from '@/components/AllInOnePlatform';
import OutputGallery from '@/components/OutputGallery';
import MoatSection from '@/components/MoatSection';
import CompetitorComparison from '@/components/CompetitorComparison';
import PricingSection from '@/components/PricingSection';
import UrgencySection from '@/components/UrgencySection';
import SecurityFooter from '@/components/SecurityFooter';
import OptimizedOnboardingV2 from '@/components/OptimizedOnboardingV2';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Check, Star, Crown, Calculator } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SdrRoiLanding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const navigate = useNavigate();

  // Handler for sign in clicks
  const handleSignInClick = () => {
    navigate('/login');
  };

  // Optimized pricing section
  const PricingSection = () => (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-green-100 text-green-800">
            Simple Pricing
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start with a 3-day free trial. No setup fees, no contracts. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Growth Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-green-50 border-2 border-green-500 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 relative"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-green-600 text-white">
                <Crown className="w-3 h-3 mr-1" />
                Most Popular
              </Badge>
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Growth</h3>
              <div className="text-4xl font-bold text-green-600 mb-1">$97</div>
              <div className="text-sm text-gray-600 mb-2">$0 today → $97 after 3-day trial</div>
              <p className="text-gray-600 mb-6">For scaling businesses</p>
              
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">150,000 emails/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">AI-powered campaigns</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Automated lead sourcing</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Meeting booking</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Email automation</span>
                </li>
              </ul>
              
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => setShowOnboarding(true)}
              >
                Start 3-Day Trial
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Pro</h3>
              <div className="text-4xl font-bold text-gray-900 mb-4">$297</div>
              <p className="text-gray-600 mb-6">For high-volume senders</p>
              
              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">500,000 emails/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Everything in Growth</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Advanced AI optimization</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">White-label options</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 mt-0.5" />
                  <span className="text-gray-700">Dedicated support</span>
                </li>
              </ul>
              
              <Button 
                variant="outline" 
                className="w-full border-gray-300"
                onClick={() => setShowOnboarding(true)}
              >
                Start Pro Trial
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Guarantee */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-50 border border-green-200 rounded-full">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">
              30-day money-back guarantee • Cancel anytime • No setup fees
            </span>
          </div>
        </div>
      </div>
    </section>
  );

  // ROI Calculator Section
  const RoiCalculatorSection = () => (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-green-100 text-green-800">
            <Calculator className="w-3 h-3 mr-1" />
            ROI Calculator
          </Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Calculate Your Savings
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how much you can save by replacing your SDR team with Munger.ai
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl p-8 shadow-lg border border-gray-200"
        >
          <div className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">SDR Costs (Monthly)</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Base Salary</span>
                      <span>$5,000</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: '60%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Benefits & Taxes</span>
                      <span>$1,500</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: '20%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Tools & Software</span>
                      <span>$500</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: '10%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Training & Management</span>
                      <span>$1,000</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: '15%' }}></div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between font-semibold">
                      <span>Total Monthly Cost</span>
                      <span className="text-red-600">$8,000</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Munger.ai Costs (Monthly)</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Subscription</span>
                      <span>$97</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Setup Time (One-time)</span>
                      <span>10 minutes</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '5%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Management Time</span>
                      <span>0 hours</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between font-semibold">
                      <span>Total Monthly Cost</span>
                      <span className="text-green-600">$97</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-6 rounded-xl border border-green-200">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Your Monthly Savings</h4>
                  <p className="text-gray-600">Replace your SDR and save:</p>
                </div>
                <div className="text-4xl font-bold text-green-600">$7,903</div>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Cost Reduction</span>
                  <span>98.8%</span>
                </div>
                <div className="w-full bg-gray-200 h-3 rounded-full">
                  <div className="bg-green-500 h-3 rounded-full" style={{ width: '98.8%' }}></div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 px-8 py-4 text-lg font-semibold"
                onClick={() => setShowOnboarding(true)}
              >
                Start Saving Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );

  // Final CTA Section
  const FinalCTASection = () => (
    <section className="py-20 bg-green-600">
      <div className="max-w-4xl mx-auto px-6 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Save $7,903 Every Month?
          </h2>
          <p className="text-xl mb-8 text-green-100">
            Join 2,100+ companies who replaced their SDRs with AI. Setup takes 10 minutes.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button 
              size="lg"
              className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              onClick={() => setShowOnboarding(true)}
            >
              Calculate Your Savings
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            
            <Button 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 text-lg font-semibold"
              onClick={() => navigate('/book-demo')}
            >
              Book Demo
            </Button>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-green-100">
              Already have an account?{' '}
              <Button 
                variant="link"
                className="p-0 h-auto text-sm underline hover:text-white transition-colors"
                onClick={handleSignInClick}
              >
                Sign in
              </Button>
            </p>
          </div>
          
          <div className="flex items-center justify-center gap-6 text-sm text-green-100">
            <span>✓ 10-minute setup</span>
            <span>✓ First replies in 24 hours</span>
            
          </div>
        </motion.div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <SdrRoiHeroSection onStartClick={() => setShowOnboarding(true)} />
      
      {/* ROI Calculator - Key feature for this landing page */}
      <RoiCalculatorSection />
      
      {/* Testimonials */}
      <TestimonialSection />
      
      {/* Urgency Section */}
      <UrgencySection onStartClick={() => setShowOnboarding(true)} />
      
      {/* Persona Toggle */}
      <PersonaToggle />
      
      {/* Accountability Section */}
      <AccountabilitySection />
      
      {/* How It Works */}
      <FeatureShowcase />
      
      {/* ROI Gallery */}
      <OutputGallery />
      
      {/* All-in-One Platform */}
      <AllInOnePlatform onStartClick={() => setShowOnboarding(true)} />
      
      {/* Competitor Comparison */}
      <CompetitorComparison onStartClick={() => setShowOnboarding(true)} />
      
      {/* Trust/Authority Section */}
      <MoatSection />
      
      {/* Pricing */}
      <PricingSection />
      
      {/* Final CTA */}
      <FinalCTASection />
      
      {/* Security Footer */}
      <SecurityFooter />
      
      {/* Optimized Onboarding Modal */}
      <OptimizedOnboardingV2 
        isOpen={showOnboarding} 
        onClose={() => setShowOnboarding(false)} 
      />
    </div>
  );
};

export default SdrRoiLanding; 